import { useState, useLayoutEffect, useEffect } from "react";

import {
  Button,
  InfoModal,
  ErrorFilledSVG,
  Icon,
  ChevronLeftSVG,
} from "@newsela/angelou";
import PropTypes from "prop-types";

import StudentSelection from "@/students/StudentSelection/StudentSelection";
import ClassroomSelectionSGA from "@/classrooms/ClassroomSelection/ClassroomSelectionSGA";
import {
  getFullUserName,
  assigneeSelectionSteps,
} from "../../utils/assignmentUtils";
import AssigneeTypeSelection from "../AssigneeTypeSelection";
import DeselectedAssignees from "../DeselectedAssignees";
import {
  assigneeModalbuttonRowCss,
  assigneeModalbuttonContainer,
  assigneeModalErrorCss,
  modalContentWrapperCss,
  footerRowCss,
} from "./AssigneeSelectorModal.style";

const {
  ASSIGNEE_TYPE_SELECTION_STEP,
  CLASSROOMS_SELECTION_STEP,
  STUDENTS_SELECTION_STEP,
  DESELECT_CONFIRMATION_STEP,
} = assigneeSelectionSteps;

const propTypes = {
  assigneeSelectorModalProps: PropTypes.object,
  assignmentClassrooms: PropTypes.array,
  assignmentStudentAssignees: PropTypes.array,
  classroomChoices: PropTypes.array,
  currentSelectionOfClassrooms: PropTypes.array,
  currentSelectionOfStudentAssignees: PropTypes.array,
  setAssigneeSelectorModalActive: PropTypes.func,
  setCurrentSelectionOfClassrooms: PropTypes.func,
  setCurrentSelectionOfStudentAssignees: PropTypes.func,
  setAssigneeSelectionStep: PropTypes.func,
  assigneeSelectionStep: PropTypes.number,
  isEditPage: PropTypes.bool,
  windowWidth: PropTypes.number,
};

const AssigneeSelectorModal = ({
  windowWidth,
  isEditPage,
  assigneeSelectionStep,
  assigneeSelectorModalProps,
  assignmentClassrooms,
  assignmentStudentAssignees,
  classroomChoices,
  currentSelectionOfClassrooms,
  currentSelectionOfStudentAssignees,
  setAssigneeSelectorModalActive,
  setCurrentSelectionOfClassrooms,
  setCurrentSelectionOfStudentAssignees,
  setAssigneeSelectionStep,
}) => {
  const [assigneeSelectionTypeOptions, setAssigneeSelectionTypeOptions] =
    useState("classrooms");
  const [errorMessage, setErrorMessage] = useState(false);
  const [globalLayoutHeaderHeight, setGlobalLayoutHeaderHeight] = useState("");
  const [stagedClassroomSelection, setStagedClassroomSelection] = useState([
    ...currentSelectionOfClassrooms,
  ]);
  const [stagedStudentAssigneeSelection, setStagedStudentAssigneeSelection] =
    useState([...currentSelectionOfStudentAssignees]);

  const [deselectedClassrooms, setDeselectedClassrooms] = useState([]);
  const [deselectedStudents, setDeselectedStudents] = useState([]);
  const [isDeselected, setIsDeselected] = useState(false);

  const initializeAssigneeType = () => {
    const assigneeType = currentSelectionOfStudentAssignees.length
      ? "students"
      : "classrooms";
    if (assigneeSelectionTypeOptions !== assigneeType) {
      setAssigneeSelectionTypeOptions(assigneeType);
    }
  };

  const handleSelectedAssignees = () => {
    const isClassroomsSelectionType =
      assigneeSelectionTypeOptions === "classrooms";
    const isStudentsSelectionType = assigneeSelectionTypeOptions === "students";
    const hasStagedClassroomSelection = stagedClassroomSelection.length;
    const hasStagedStudentAssigneeSelection =
      stagedStudentAssigneeSelection.length;
    if (
      (isClassroomsSelectionType && !hasStagedClassroomSelection) ||
      (isStudentsSelectionType && !hasStagedStudentAssigneeSelection)
    ) {
      setErrorMessage(true);
    } else if (isEditPage && isDeselected) {
      setAssigneeSelectionStep(DESELECT_CONFIRMATION_STEP);
    } else if (isClassroomsSelectionType) {
      setStagedStudentAssigneeSelection([]);
      setCurrentSelectionOfStudentAssignees([]);
      setCurrentSelectionOfClassrooms([...stagedClassroomSelection]);
      setAssigneeSelectorModalActive(false);
      setErrorMessage(false);
    } else if (isStudentsSelectionType) {
      setStagedClassroomSelection([]);
      setCurrentSelectionOfClassrooms([]);
      setCurrentSelectionOfStudentAssignees([
        ...stagedStudentAssigneeSelection,
      ]);
      setAssigneeSelectorModalActive(false);
      setErrorMessage(false);
    }
  };

  const handleAssigneeSelectionType = () => {
    const isStudentsSelectionType = assigneeSelectionTypeOptions === "students";
    if (isStudentsSelectionType) {
      setAssigneeSelectionStep(STUDENTS_SELECTION_STEP);
    } else {
      setAssigneeSelectionStep(CLASSROOMS_SELECTION_STEP);
    }
  };

  const handleIconBackButton = () => {
    if (!isEditPage) {
      setCurrentSelectionOfClassrooms([]);
      setCurrentSelectionOfStudentAssignees([]);
      setAssigneeSelectionTypeOptions("classrooms");
    }
    setAssigneeSelectionStep(ASSIGNEE_TYPE_SELECTION_STEP);
    setErrorMessage(false);
  };

  const handleDeselectedAssignees = () => {
    const hasStagedClassroomSelection = stagedClassroomSelection.length;
    const hasStagedStudentAssigneeSelection =
      stagedStudentAssigneeSelection.length;
    if (hasStagedClassroomSelection) {
      setStagedStudentAssigneeSelection([]);
      setCurrentSelectionOfStudentAssignees([]);
      setCurrentSelectionOfClassrooms([...stagedClassroomSelection]);
    }
    if (hasStagedStudentAssigneeSelection) {
      setStagedClassroomSelection([]);
      setCurrentSelectionOfClassrooms([]);
      setCurrentSelectionOfStudentAssignees([
        ...stagedStudentAssigneeSelection,
      ]);
    }
    setAssigneeSelectorModalActive(false);
  };

  const renderProperModalPage = () => {
    switch (assigneeSelectionStep) {
      case ASSIGNEE_TYPE_SELECTION_STEP:
        return (
          <AssigneeTypeSelection
            setAssigneeSelectionTypeOptions={setAssigneeSelectionTypeOptions}
            assigneeSelectionTypeOptions={assigneeSelectionTypeOptions}
          />
        );
      case CLASSROOMS_SELECTION_STEP:
        return (
          <ClassroomSelectionSGA
            classroomChoices={classroomChoices}
            stagedClassroomSelection={stagedClassroomSelection}
            setStagedClassroomSelection={setStagedClassroomSelection}
            setStagedStudentAssigneeSelection={
              setStagedStudentAssigneeSelection
            }
          />
        );
      case STUDENTS_SELECTION_STEP:
        return (
          <StudentSelection
            classroomChoices={classroomChoices}
            stagedStudentAssigneeSelection={stagedStudentAssigneeSelection}
            setStagedClassroomSelection={setStagedClassroomSelection}
            setStagedStudentAssigneeSelection={
              setStagedStudentAssigneeSelection
            }
          />
        );
      case DESELECT_CONFIRMATION_STEP:
        return (
          <DeselectedAssignees
            assigneeSelectionTypeOptions={assigneeSelectionTypeOptions}
            deselectedClassrooms={deselectedClassrooms}
            deselectedStudents={deselectedStudents}
          />
        );
      default:
        break;
    }
  };

  const renderFooterButton = () => {
    switch (assigneeSelectionStep) {
      case ASSIGNEE_TYPE_SELECTION_STEP:
        return (
          <Button
            onClick={() => {
              handleAssigneeSelectionType();
            }}
          >
            Select
          </Button>
        );
      case CLASSROOMS_SELECTION_STEP:
      case STUDENTS_SELECTION_STEP:
        return (
          <Button
            onClick={() => {
              handleSelectedAssignees();
            }}
          >
            Save
          </Button>
        );
      case DESELECT_CONFIRMATION_STEP:
        return (
          <Button
            onClick={() => {
              handleDeselectedAssignees();
            }}
          >
            Deselect assignees
          </Button>
        );
      default:
        break;
    }
  };

  const renderErrorMessage = () => {
    const isClassroomsSelectionType =
      assigneeSelectionTypeOptions === "classrooms";
    return (
      <div css={assigneeModalErrorCss}>
        <Icon SvgComponent={ErrorFilledSVG} /> Select at least 1{" "}
        {isClassroomsSelectionType ? "class" : "student"} to finish
      </div>
    );
  };

  useLayoutEffect(() => {
    const globalLayoutHeader =
      document.querySelectorAll("header")[0]?.clientHeight;
    setGlobalLayoutHeaderHeight(globalLayoutHeader);
  }, [windowWidth]);

  // an effect to calculate deselected classrooms and set in state:
  useEffect(() => {
    const currentlyDeselectedClassrooms = assignmentClassrooms
      .filter(
        (classroom) =>
          !stagedClassroomSelection
            .map((classroom) => classroom?.id)
            .includes(classroom.id),
      )
      .map((payload) => payload.name);
    setDeselectedClassrooms(currentlyDeselectedClassrooms);
  }, [stagedClassroomSelection]);

  // an effect to calculate deselected students and set in state:
  useEffect(() => {
    const currentlyDeselectedStudents = assignmentStudentAssignees
      .filter(
        (student) =>
          !stagedStudentAssigneeSelection
            .map((student) => student.unique_id)
            .includes(student.unique_id),
      )
      .map((user) => getFullUserName(user));
    setDeselectedStudents(currentlyDeselectedStudents);
  }, [stagedStudentAssigneeSelection]);

  // an effect to calculate if any assignees are deselected, then set in state:
  useEffect(() => {
    const deselected = Boolean(
      deselectedClassrooms.length > 0 || deselectedStudents.length > 0,
    );
    if (isDeselected !== deselected) {
      setIsDeselected(deselected);
    }
  }, [deselectedStudents, deselectedClassrooms]);

  useEffect(initializeAssigneeType, [currentSelectionOfStudentAssignees]);

  return (
    <>
      <InfoModal
        labelId="AssigneeSelector"
        title="Select assignees"
        {...assigneeSelectorModalProps}
        __cssFor={{
          modalContentWrapper: modalContentWrapperCss(globalLayoutHeaderHeight),
          root: { zIndex: "10" },
          footer: footerRowCss,
        }}
        footer={
          <div css={assigneeModalbuttonContainer}>
            <div>
              {assigneeSelectionStep !== 0 ? (
                <Button
                  flavor={Button.flavors.tertiary}
                  onClick={() => {
                    handleIconBackButton();
                  }}
                  icon={{
                    SvgComponent: ChevronLeftSVG,
                    alignment: Button.iconAlignments.left,
                  }}
                >
                  Back
                </Button>
              ) : null}
            </div>
            <div css={assigneeModalbuttonRowCss}>
              {assigneeSelectionStep !== 3 ? (
                <Button
                  flavor={Button.flavors.polarSecondary}
                  onClick={() => {
                    setAssigneeSelectorModalActive(false);
                    setErrorMessage(false);
                  }}
                >
                  Cancel
                </Button>
              ) : null}
              {renderFooterButton()}
            </div>
          </div>
        }
        onClose={() => {
          initializeAssigneeType();
          setStagedStudentAssigneeSelection([
            ...currentSelectionOfStudentAssignees,
          ]);
          setStagedClassroomSelection([...currentSelectionOfClassrooms]);
          setErrorMessage(false);
        }}
      >
        {errorMessage ? renderErrorMessage() : null}
        {renderProperModalPage()}
      </InfoModal>
    </>
  );
};

AssigneeSelectorModal.propTypes = propTypes;
export default AssigneeSelectorModal;
